import { useEffect, useState } from 'react'
import { api } from '../../api/client'
import type {
  CreateTouchpointDirectionApi,
  CreateTouchpointTypeApi,
  Lead,
  LeadStage,
  LeadTouchpoint,
} from '../../types'
import Badge, { type BadgeColor } from '../Badge'
import { fmtDate } from '../../lib/format'

// Stage transitions allowed FROM each non-terminal stage. Terminal stages
// (won, lost, won_to_royal_buyer) are not in this map — UI hides the
// dropdown for them.
const STAGE_FORWARD: Record<string, LeadStage[]> = {
  new: ['attempted', 'contacted', 'qualified', 'lost', 'won_to_royal_buyer'],
  attempted: ['contacted', 'qualified', 'lost', 'won_to_royal_buyer'],
  contacted: ['qualified', 'consulted', 'lost', 'won_to_royal_buyer'],
  qualified: ['consulted', 'charrette', 'lost', 'won_to_royal_buyer'],
  consulted: ['charrette', 'proposal', 'lost', 'won_to_royal_buyer'],
  charrette: ['proposal', 'lost', 'won_to_royal_buyer'],
  proposal: ['negotiating', 'won', 'lost', 'won_to_royal_buyer'],
  negotiating: ['won', 'lost', 'won_to_royal_buyer'],
}

const TERMINAL = new Set(['won', 'lost', 'won_to_royal_buyer'])

function contactStateColor(s: Lead['contact_state']): BadgeColor {
  if (s === 'contacted') return 'green'
  if (s === 'attempted') return 'gold'
  return 'red'
}

function stageColor(s: LeadStage): BadgeColor {
  if (s === 'won') return 'green'
  if (s === 'lost') return 'red'
  if (s === 'won_to_royal_buyer') return 'blue'
  return 'gray'
}

function touchpointIcon(type: LeadTouchpoint['type']): string {
  switch (type) {
    case 'call':
    case 'kixie_call':
      return '☎'
    case 'text':
      return '💬'
    case 'auto_text':
      return '🤖'
    case 'email':
    case 'email_open':
    case 'email_click':
      return '✉'
    case 'meeting':
    case 'site_visit':
    case 'charrette':
      return '🤝'
    case 'note':
      return '📝'
    case 'form_submission':
      return '📋'
    case 'calendly_booking':
      return '📅'
    default:
      return '•'
  }
}

export default function LeadDetailView({
  leadId,
  onBack,
  onChanged,
}: {
  leadId: string
  onBack: () => void
  onChanged?: () => void
}) {
  const [lead, setLead] = useState<Lead | null>(null)
  const [touchpoints, setTouchpoints] = useState<LeadTouchpoint[]>([])
  const [computed, setComputed] = useState<{
    days_since_created: number
    days_since_last_contact: number | null
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [showTouchpointModal, setShowTouchpointModal] = useState(false)
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)

  async function refresh() {
    try {
      const res = await api.marketingLeads.get(leadId)
      setLead(res.lead)
      setTouchpoints(res.touchpoints)
      setComputed(res.computed)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  useEffect(() => {
    void refresh()
  }, [leadId])

  async function markAttempted() {
    if (!lead) return
    setBusy(true)
    try {
      await api.marketingLeads.patch(leadId, { contact_state: 'attempted' })
      await refresh()
      onChanged?.()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function markContacted() {
    if (!lead) return
    setBusy(true)
    try {
      await api.marketingLeads.patch(leadId, { contact_state: 'contacted' })
      await refresh()
      onChanged?.()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function advanceStage(target: LeadStage) {
    if (!lead) return
    setBusy(true)
    try {
      await api.marketingLeads.patch(leadId, { stage: target })
      await refresh()
      onChanged?.()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function softDelete() {
    if (!lead) return
    if (!window.confirm(`Soft-delete lead "${lead.name ?? '(no name)'}"? Row preserved for audit.`)) return
    setBusy(true)
    try {
      await api.marketingLeads.delete(leadId)
      onChanged?.()
      onBack()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (!lead) {
    return (
      <section>
        <button className="btn-ghost small" onClick={onBack}>
          ← Back to inbox
        </button>
        {error ? (
          <div className="card alert mt-2">{error}</div>
        ) : (
          <p className="muted mt-2">Loading…</p>
        )}
      </section>
    )
  }

  const nextStages = TERMINAL.has(lead.stage) ? [] : STAGE_FORWARD[lead.stage] ?? []
  const royalBuyer = lead.royal_buyer_routed === 1
  const royalListing = lead.royal_listing_routed === 1

  return (
    <section>
      <button className="btn-ghost small" onClick={onBack}>
        ← Back to inbox
      </button>

      {error && <div className="card alert mt-2 mb-2">{error}</div>}

      {/* Header */}
      <div className="card mt-2 mb-2">
        <div className="row-between">
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0 }}>{lead.name ?? '(no name)'}</h2>
            <div className="muted small mt-1">
              {lead.email ?? '—'} · {lead.phone ?? '—'} · created {fmtDate(lead.created_at)}
            </div>
            <div className="row gap-2 mt-2" style={{ flexWrap: 'wrap' }}>
              <Badge color={stageColor(lead.stage)}>stage: {lead.stage}</Badge>
              <Badge color={contactStateColor(lead.contact_state)}>
                {lead.contact_state}
              </Badge>
              <Badge color="gray">source: {lead.source}</Badge>
              {lead.city_target && <Badge color="gray">{lead.city_target}</Badge>}
              {lead.pricing_tier_estimated !== 'unknown' && (
                <Badge color="gray">{lead.pricing_tier_estimated}</Badge>
              )}
              {royalBuyer && <Badge color="blue">Royal Buyer Track</Badge>}
              {royalListing && <Badge color="blue">Royal Listing Track</Badge>}
              {lead.assigned_to && <Badge color="gray">→ {lead.assigned_to}</Badge>}
            </div>
            <div className="muted small mt-2">
              {computed?.days_since_created ?? 0}d since created
              {computed?.days_since_last_contact !== null &&
                computed?.days_since_last_contact !== undefined &&
                ` · ${computed.days_since_last_contact}d since last touchpoint`}
            </div>
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="card mb-2">
        <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
          {lead.contact_state === 'uncontacted' && (
            <button
              className="btn-primary small"
              onClick={markAttempted}
              disabled={busy}
            >
              Mark Attempted
            </button>
          )}
          {(lead.contact_state === 'uncontacted' || lead.contact_state === 'attempted') && (
            <button
              className="btn-primary small"
              onClick={markContacted}
              disabled={busy}
            >
              Mark Contacted
            </button>
          )}
          {nextStages.length > 0 && (
            <select
              onChange={(e) => {
                const v = e.target.value
                if (v) void advanceStage(v as LeadStage)
                e.target.value = ''
              }}
              defaultValue=""
              disabled={busy}
            >
              <option value="">Advance stage…</option>
              {nextStages.map((s) => (
                <option key={s} value={s}>
                  → {s}
                </option>
              ))}
            </select>
          )}
          <button
            className="btn-ghost small"
            onClick={() => setShowTouchpointModal(true)}
            disabled={busy}
          >
            + Log Touchpoint
          </button>
          <button
            className="btn-ghost small"
            onClick={() => setShowNoteModal(true)}
            disabled={busy}
          >
            + Add Note
          </button>
          <button
            className="btn-ghost small"
            onClick={() => setShowEditModal(true)}
            disabled={busy}
          >
            Edit Lead
          </button>
          <button
            className="btn-ghost small"
            onClick={softDelete}
            disabled={busy}
            style={{ marginLeft: 'auto', color: '#c53030' }}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Lead fields */}
      <div className="card mb-2">
        <h3 style={{ marginTop: 0 }}>Lead details</h3>
        <KV k="Lot status" v={lead.lot_status ?? '—'} />
        <KV k="Existing home" v={lead.existing_home_status ?? '—'} />
        <KV k="City target" v={lead.city_target ?? '—'} />
        <KV k="Pricing tier (est.)" v={lead.pricing_tier_estimated} />
        <KV k="Project size" v={lead.project_size_stated ?? '—'} />
        <KV k="Timeline" v={lead.timeline_stated ?? '—'} />
        {lead.message && <KV k="Prospect message" v={lead.message} multiline />}
        {lead.notes && <KV k="Internal notes" v={lead.notes} multiline />}
      </div>

      {/* Royal track section */}
      {(royalBuyer || royalListing) && (
        <details className="card mb-2" open>
          <summary style={{ cursor: 'pointer' }}>
            <strong>Royal Home Team tracks</strong>
          </summary>
          {royalBuyer && (
            <div className="mt-2">
              <KV k="Buyer outcome" v={lead.royal_buyer_outcome ?? 'in_search'} />
              <KV k="Buyer commission (est.)" v={moneyOrDash(lead.royal_buyer_commission_estimated)} />
              <KV k="Buyer commission (actual)" v={moneyOrDash(lead.royal_buyer_commission)} />
            </div>
          )}
          {royalListing && (
            <div className="mt-2">
              <KV k="Listing commission (est.)" v={moneyOrDash(lead.royal_listing_commission_estimated)} />
              <KV k="Listing commission (actual)" v={moneyOrDash(lead.royal_listing_commission)} />
            </div>
          )}
        </details>
      )}

      {/* Touchpoint timeline */}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Touchpoint timeline</h3>
        {touchpoints.length === 0 ? (
          <p className="muted small">No touchpoints yet.</p>
        ) : (
          <div>
            {touchpoints.map((tp) => (
              <div
                key={tp.id}
                style={{
                  borderLeft: tp.direction === 'inbound' ? '3px solid #2ea043' : tp.direction === 'outbound' ? '3px solid #d4af37' : '3px solid #999',
                  paddingLeft: 10,
                  marginBottom: 10,
                }}
              >
                <div className="small">
                  <span style={{ marginRight: 6 }}>{touchpointIcon(tp.type)}</span>
                  <strong>{tp.type}</strong> · {tp.direction} · {fmtDate(tp.timestamp)}
                  {tp.actor_email && tp.actor_email !== 'unknown' && (
                    <span className="muted"> · {tp.actor_email}</span>
                  )}
                </div>
                {tp.content_summary && (
                  <div className="small mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                    {tp.content_summary}
                  </div>
                )}
                {tp.outcome && (
                  <div className="small muted mt-1">outcome: {tp.outcome}</div>
                )}
                {tp.duration_seconds && (
                  <div className="small muted mt-1">
                    duration: {Math.round(tp.duration_seconds / 60)} min
                  </div>
                )}
                {tp.recording_url && (
                  <div className="small mt-1">
                    <a className="link" href={tp.recording_url} target="_blank" rel="noreferrer">
                      Recording
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showTouchpointModal && (
        <TouchpointModal
          leadId={leadId}
          onClose={() => setShowTouchpointModal(false)}
          onSaved={() => {
            setShowTouchpointModal(false)
            void refresh()
            onChanged?.()
          }}
        />
      )}
      {showNoteModal && (
        <NoteModal
          leadId={leadId}
          onClose={() => setShowNoteModal(false)}
          onSaved={() => {
            setShowNoteModal(false)
            void refresh()
          }}
        />
      )}
      {showEditModal && (
        <EditLeadModal
          lead={lead}
          onClose={() => setShowEditModal(false)}
          onSaved={() => {
            setShowEditModal(false)
            void refresh()
            onChanged?.()
          }}
        />
      )}
    </section>
  )
}

// ---------- helpers ----------

function KV({ k, v, multiline }: { k: string; v: string; multiline?: boolean }) {
  return (
    <div className="small mb-1">
      <span className="muted">{k}: </span>
      {multiline ? (
        <span style={{ whiteSpace: 'pre-wrap' }}>{v}</span>
      ) : (
        <span>{v}</span>
      )}
    </div>
  )
}

function moneyOrDash(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
}

// ---------- modals ----------

function TouchpointModal({
  leadId,
  onClose,
  onSaved,
}: {
  leadId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [type, setType] = useState<CreateTouchpointTypeApi>('call')
  const [direction, setDirection] = useState<CreateTouchpointDirectionApi>('outbound')
  const [summary, setSummary] = useState('')
  const [durationMin, setDurationMin] = useState('')
  const [outcome, setOutcome] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await api.marketingLeads.addTouchpoint(leadId, {
        type,
        direction,
        summary: summary || undefined,
        duration_minutes: durationMin ? parseInt(durationMin, 10) : undefined,
        outcome: outcome || undefined,
      })
      onSaved()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Log touchpoint</h3>
        {error && <div className="card alert mb-2">{error}</div>}
        <label className="form-row">
          <span>Type</span>
          <select value={type} onChange={(e) => setType(e.target.value as CreateTouchpointTypeApi)}>
            <option value="call">call</option>
            <option value="text">text</option>
            <option value="email">email</option>
            <option value="in_person">in_person (meeting)</option>
            <option value="note">note</option>
            <option value="other">other</option>
          </select>
        </label>
        <label className="form-row">
          <span>Direction</span>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as CreateTouchpointDirectionApi)}
          >
            <option value="outbound">outbound (we contacted them)</option>
            <option value="inbound">inbound (they contacted us)</option>
            <option value="na">neutral / na</option>
          </select>
        </label>
        <label className="form-row">
          <span>Summary</span>
          <textarea
            rows={3}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="What happened?"
          />
        </label>
        <label className="form-row">
          <span>Duration (minutes)</span>
          <input
            value={durationMin}
            onChange={(e) => setDurationMin(e.target.value)}
            placeholder="e.g. 12"
            inputMode="numeric"
          />
        </label>
        <label className="form-row">
          <span>Outcome</span>
          <input
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            placeholder="left voicemail / scheduled call / etc."
          />
        </label>
        <div className="row gap-2 mt-3">
          <button className="btn-primary small" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save touchpoint'}
          </button>
          <button className="btn-ghost small" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

function NoteModal({
  leadId,
  onClose,
  onSaved,
}: {
  leadId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [content, setContent] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    if (!content.trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.marketingLeads.addNote(leadId, content.trim())
      onSaved()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Add internal note</h3>
        {error && <div className="card alert mb-2">{error}</div>}
        <label className="form-row">
          <span>Note</span>
          <textarea
            rows={5}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Internal note (visible only to Heritage)"
            autoFocus
          />
        </label>
        <div className="row gap-2 mt-3">
          <button className="btn-primary small" onClick={save} disabled={busy || !content.trim()}>
            {busy ? 'Saving…' : 'Save note'}
          </button>
          <button className="btn-ghost small" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

const PATCHABLE_KEYS = [
  'name',
  'email',
  'phone',
  'lot_status',
  'existing_home_status',
  'city_target',
  'pricing_tier_estimated',
  'project_size_stated',
  'timeline_stated',
  'message',
  'notes',
  'royal_buyer_outcome',
  'royal_buyer_commission_estimated',
  'royal_buyer_commission',
  'royal_listing_commission_estimated',
  'royal_listing_commission',
  'lost_reason',
  'competitor_mentioned',
  'assigned_to',
] as const

function EditLeadModal({
  lead,
  onClose,
  onSaved,
}: {
  lead: Lead
  onClose: () => void
  onSaved: () => void
}) {
  const [draft, setDraft] = useState<Partial<Lead>>(() => {
    const init: Partial<Lead> = {}
    for (const k of PATCHABLE_KEYS) {
      ;(init as Record<string, unknown>)[k] = (lead as unknown as Record<string, unknown>)[k] ?? null
    }
    return init
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function setField<K extends keyof Lead>(k: K, v: Lead[K]) {
    setDraft((prev) => ({ ...prev, [k]: v }))
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await api.marketingLeads.patch(lead.id, draft)
      onSaved()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <h3>Edit lead</h3>
        {error && <div className="card alert mb-2">{error}</div>}

        <label className="form-row">
          <span>Name</span>
          <input
            value={(draft.name as string) ?? ''}
            onChange={(e) => setField('name', e.target.value || null)}
          />
        </label>
        <label className="form-row">
          <span>Email</span>
          <input
            value={(draft.email as string) ?? ''}
            onChange={(e) => setField('email', e.target.value || null)}
          />
        </label>
        <label className="form-row">
          <span>Phone</span>
          <input
            value={(draft.phone as string) ?? ''}
            onChange={(e) => setField('phone', e.target.value || null)}
          />
        </label>
        <label className="form-row">
          <span>Assigned to</span>
          <select
            value={(draft.assigned_to as string) ?? ''}
            onChange={(e) => setField('assigned_to', (e.target.value || null) as Lead['assigned_to'])}
          >
            <option value="">—</option>
            <option value="lori">lori</option>
            <option value="dustin">dustin</option>
          </select>
        </label>
        <label className="form-row">
          <span>City target</span>
          <select
            value={(draft.city_target as string) ?? ''}
            onChange={(e) => setField('city_target', (e.target.value || null) as Lead['city_target'])}
          >
            <option value="">—</option>
            <option value="woodbury">woodbury</option>
            <option value="afton">afton</option>
            <option value="lake_elmo">lake_elmo</option>
            <option value="stillwater">stillwater</option>
            <option value="other">other</option>
          </select>
        </label>
        <label className="form-row">
          <span>Pricing tier (est.)</span>
          <select
            value={(draft.pricing_tier_estimated as string) ?? 'unknown'}
            onChange={(e) =>
              setField('pricing_tier_estimated', e.target.value as Lead['pricing_tier_estimated'])
            }
          >
            <option value="unknown">unknown</option>
            <option value="tier_1">tier_1 ($1.5M-2M)</option>
            <option value="tier_2">tier_2 ($2M-2.75M)</option>
            <option value="tier_3">tier_3 ($2.75M-4M)</option>
            <option value="tier_4">tier_4 ($4M+)</option>
          </select>
        </label>
        <label className="form-row">
          <span>Lot status</span>
          <select
            value={(draft.lot_status as string) ?? ''}
            onChange={(e) => setField('lot_status', (e.target.value || null) as Lead['lot_status'])}
          >
            <option value="">—</option>
            <option value="own">own</option>
            <option value="under_contract">under_contract</option>
            <option value="looking">looking</option>
            <option value="heritage_helps">heritage_helps</option>
          </select>
        </label>
        <label className="form-row">
          <span>Existing home</span>
          <select
            value={(draft.existing_home_status as string) ?? ''}
            onChange={(e) =>
              setField(
                'existing_home_status',
                (e.target.value || null) as Lead['existing_home_status']
              )
            }
          >
            <option value="">—</option>
            <option value="selling">selling</option>
            <option value="keeping">keeping</option>
            <option value="relocating">relocating</option>
            <option value="rents">rents</option>
          </select>
        </label>
        <label className="form-row">
          <span>Notes</span>
          <textarea
            rows={4}
            value={(draft.notes as string) ?? ''}
            onChange={(e) => setField('notes', e.target.value || null)}
          />
        </label>

        <div className="row gap-2 mt-3">
          <button className="btn-primary small" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
          <button className="btn-ghost small" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
